import * as React from "react";
import { Box, Typography, useTheme, MenuItem, TextField } from "@mui/material";
import { useSelector } from "react-redux";
import { useState } from "react";
import moment from "moment";

import DashboardIcon  from "@mui/icons-material/Dashboard";
import FilterListIcon from "@mui/icons-material/FilterList";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";

import LineChart from "components/Graphs/LineChart";
import { useGetBusinessBranchList }    from "api/hooks/business";
import { useYearsList }                from "api/hooks/inventory";
import { useBusinessBranchDashboard }  from "api/hooks/dashboards";

const inputSx = (cardBg, border) => ({
  "& .MuiInputBase-root": { fontSize: "13px", height: "36px", backgroundColor: cardBg, borderRadius: "8px" },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: border },
  "& .MuiInputLabel-root": { fontSize: "12px" },
});

const StatCard = ({ label, value, accent, textPri, textSec, cardBg, border, alert = false }) => (
  <Box sx={{
    flex: "1 1 150px",
    backgroundColor: alert ? (cardBg === "#ffffff" ? "#fff8f8" : "rgba(226,75,74,0.06)") : cardBg,
    border: `1px solid ${alert ? "rgba(226,75,74,0.25)" : border}`,
    borderRadius: "12px",
    p: "1rem 1.25rem",
    display: "flex", flexDirection: "column", gap: "6px",
  }}>
    <Typography sx={{ fontSize: "11px", fontWeight: 500, letterSpacing: "0.08em", textTransform: "uppercase", color: alert ? "#e24b4a" : textSec }}>
      {label}
    </Typography>
    <Typography sx={{ fontSize: "20px", fontWeight: 500, color: alert ? "#e24b4a" : textPri, lineHeight: 1.2 }}>
      {value ?? "—"}
    </Typography>
    <Box sx={{ height: "2px", width: "24px", borderRadius: "2px", backgroundColor: alert ? "#e24b4a" : accent, mt: "2px" }} />
  </Box>
);

export default function BusinessDashboard() {
  const theme  = useTheme();
  const isDark = theme.palette.mode === "dark";

  const bodyBg  = theme.palette.background.alt;
  const cardBg  = isDark ? "rgba(255,255,255,0.03)" : "#ffffff";
  const accent  = theme.palette.secondary.main;
  const textPri = theme.palette.neutral.dark;
  const textSec = theme.palette.neutral.main;
  const border  = isDark ? "rgba(29,158,117,0.14)" : "rgba(29,158,117,0.16)";

  const business_id = useSelector((s) => s.authReducer.business_id);
  const { businessBranchList } = useGetBusinessBranchList(1, business_id);
  const { businessBranchDashboard, setBusinessBranch, setPeriod } = useBusinessBranchDashboard();
  const { yearList } = useYearsList();

  const [periodSelect, setPeriodSelect] = useState(moment().format("YYYY"));

  const handlePeriodSelect = (e) => {
    setPeriod(e.target.value);
    setPeriodSelect(e.target.value);
  };

  const outOfStockCount    = businessBranchDashboard?.out_of_stock_count   ?? 0;
  const expiredStockCount  = businessBranchDashboard?.expired_stock_count  ?? 0;
  const totalNumberOfItems = businessBranchDashboard?.item_count           ?? 0;
  const purchasesInYear    = businessBranchDashboard?.year_purchase_record ?? [];
  const salesInYear        = businessBranchDashboard?.year_sales_record    ?? [];

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: bodyBg, p: "1.5rem 2rem", display: "flex", flexDirection: "column", gap: "1.5rem" }}>

      {/* ── Header ── */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "1rem" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <Box sx={{
            width: "40px", height: "40px", borderRadius: "10px",
            backgroundColor: "rgba(29,158,117,0.12)",
            display: "flex", alignItems: "center", justifyContent: "center",
            color: accent, flexShrink: 0,
          }}>
            <DashboardIcon sx={{ fontSize: "22px" }} />
          </Box>
          <Box>
            <Typography sx={{ fontFamily: "'DM Serif Display', serif", fontSize: "22px", fontWeight: 400, color: textPri, lineHeight: 1.2 }}>
              Business Dashboard
            </Typography>
            <Typography sx={{ fontSize: "12px", color: textSec, mt: "2px" }}>
              {moment().format("dddd, D MMMM YYYY")}
            </Typography>
          </Box>
        </Box>

        {/* Filter controls */}
        <Box sx={{
          display: "flex", alignItems: "center", flexWrap: "wrap", gap: "0.75rem",
          p: "0.75rem 1rem",
          backgroundColor: cardBg,
          border: `1px solid ${border}`,
          borderRadius: "10px",
        }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: "6px", color: accent }}>
            <FilterListIcon sx={{ fontSize: "16px" }} />
            <Typography sx={{ fontSize: "11px", fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.07em" }}>
              Filters
            </Typography>
          </Box>

          <TextField label="Business Branch" size="small" select
            onChange={(e) => setBusinessBranch(e.target.value)}
            sx={{ ...inputSx(cardBg, border), width: "180px" }}>
            {businessBranchList.map((row) => (
              <MenuItem key={row.id} value={row.id}>{row.branch_name}</MenuItem>
            ))}
          </TextField>

          <TextField label="Year" size="small" select
            defaultValue={moment().format("YYYY")}
            onChange={handlePeriodSelect}
            sx={{ ...inputSx(cardBg, border), width: "110px" }}>
            {yearList.map((y) => (
              <MenuItem key={y.year} value={y.year}>{y.year}</MenuItem>
            ))}
          </TextField>
        </Box>
      </Box>

      {/* ── Stat cards ── */}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: "1rem" }}>
        <StatCard label="Sales"         value="—"                accent={accent} textPri={textPri} textSec={textSec} cardBg={cardBg} border={border} />
        <StatCard label="Payments Due"  value="—"                accent={accent} textPri={textPri} textSec={textSec} cardBg={cardBg} border={border} />
        <StatCard label="Credit"        value="—"                accent={accent} textPri={textPri} textSec={textSec} cardBg={cardBg} border={border} />
        <StatCard label="Stock Items"   value={totalNumberOfItems} accent={accent} textPri={textPri} textSec={textSec} cardBg={cardBg} border={border} />
        <StatCard label="Out of Stock"  value={outOfStockCount}  accent={accent} textPri={textPri} textSec={textSec} cardBg={cardBg} border={border} alert />
        <StatCard label="Expired"       value={expiredStockCount} accent={accent} textPri={textPri} textSec={textSec} cardBg={cardBg} border={border} alert />
      </Box>

      {/* ── Chart ── */}
      <Box sx={{
        backgroundColor: cardBg,
        border: `1px solid ${border}`,
        borderRadius: "12px",
        p: "1.25rem 1.5rem",
      }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "10px", mb: "1rem" }}>
          <Box sx={{
            width: "32px", height: "32px", borderRadius: "8px",
            backgroundColor: "rgba(29,158,117,0.12)",
            display: "flex", alignItems: "center", justifyContent: "center",
            color: accent, flexShrink: 0,
          }}>
            <TrendingUpIcon sx={{ fontSize: "18px" }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: "14px", fontWeight: 500, color: textPri }}>
              Monthly Sales & Purchases
            </Typography>
            <Typography sx={{ fontSize: "11px", color: textSec, mt: "1px" }}>
              Year-to-date comparison for {periodSelect}
            </Typography>
          </Box>
        </Box>
        <LineChart
          label={["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"]}
          data={[
            {
              label: `Sales ${periodSelect}`,
              data: salesInYear,
              fill: false,
              borderColor: accent,
              backgroundColor: "rgba(29,158,117,0.08)",
              tension: 0.3,
            },
            {
              label: `Purchases ${periodSelect}`,
              data: purchasesInYear,
              fill: false,
              borderColor: "#108ED4",
              backgroundColor: "rgba(16,142,212,0.08)",
              tension: 0.3,
            },
          ]}
        />
      </Box>
    </Box>
  );
}